// src/components/ResourceSection.js
import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";

const ResourceSection = ({ resources, isDesktop }) => {
  const location = useLocation();
  const sectionRefs = useRef({});

  useEffect(() => {
    // Create a ref for each section
    resources.forEach((section) => {
      const sectionId = section.title.toLowerCase().replace(/\s+/g, "-");
      sectionRefs.current[sectionId] = React.createRef();
    });
  }, [resources]);

  useEffect(() => {
    // If InfoCenter passed a specific { state: { section: '...' } }, scroll to it
    if (location.state && location.state.section && sectionRefs.current[location.state.section]) {
      setTimeout(() => {
        const ref = sectionRefs.current[location.state.section];
        if (ref && ref.current) {
          ref.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 300);
    }
  }, [location.state]);

  const renderSection = (section, index) => {
    const { title, items, image } = section;
    const sectionId = title.toLowerCase().replace(/\s+/g, "-");

    return (
      <div
        key={`section-${index}`}
        ref={sectionRefs.current[sectionId] || null}
        id={sectionId}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: index * 0.2 }}
          style={{
            // On desktop, use card-like styling; on mobile, make background transparent
            width: "100%",
            maxWidth: isDesktop ? "840px" : "100%",
            margin: "1.25rem auto 2rem",
            backgroundColor: isDesktop ? "#fff" : "transparent",
            borderRadius: isDesktop ? "15px" : "0",
            boxShadow: isDesktop ? "0 4px 24px rgba(0, 0, 0, 0.1)" : "none",
            padding: isDesktop ? "1.25rem" : "0 0.25rem", 
            overflow: "hidden",
          }}
        >
          {/* Section image (if present) */}
          {image && (
            <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
              <img
                src={image}
                alt={title}
                referrerPolicy="no-referrer"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: isDesktop ? "10px" : "0",
                }}
              />
            </div>
          )}

          <h2
            style={{
              fontSize: isDesktop ? "1.5rem" : "1.25rem",
              fontWeight: "bold",
              marginBottom: "1rem",
              color: "#1a1a1a",
              textAlign: "center",
              // Only show bottom border on desktop
              borderBottom: isDesktop ? "2px solid #f0f0f0" : "none",
              paddingBottom: isDesktop ? "1rem" : "0",
            }}
          >
            {title}
          </h2>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            {items.map((item, idx) => (
              <ResourceItem
                key={`resource-${idx}`}
                item={item}
                index={idx}
                isDesktop={isDesktop}
              />
            ))}
          </div>
        </motion.div>

      </div>
    );
  };

  return (
    <div>
      {resources.map((section, index) => renderSection(section, index))}
    </div>
  );
};

const ResourceItem = ({ item, index, isDesktop }) => {
  const { title, url, description, dateAdded } = item;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      whileHover={{
        scale: 1.02,
        boxShadow: isDesktop ? "0 8px 30px rgba(0, 0, 0, 0.12)" : "none",
      }}
      style={{
        // For the item “cards,” only show a background & box shadow on desktop
        backgroundColor: isDesktop ? "#f8f9fa" : "transparent",
        borderRadius: isDesktop ? "12px" : "0",
        padding: isDesktop ? "1rem" : "0.75rem 0",
        border: isDesktop ? "1px solid #eaeaea" : "none",
        boxShadow: isDesktop ? "0 4px 12px rgba(0, 0, 0, 0.05)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      {/* Title + Date */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "0.5rem",
        }}
      >
        <h3
          style={{
            fontSize: isDesktop ? "1.15rem" : "1.05rem",
            fontWeight: "600",
            color: "#0066cc",
            margin: 0,
          }}
        >
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#0066cc",
              textDecoration: "none",
              transition: "color 0.2s ease",
            }}
          >
            {title}
          </a>
        </h3>

        {dateAdded && (
          <span
            style={{
              fontSize: "0.8rem",
              color: "#666",
              backgroundColor: isDesktop ? "#eaeaea" : "transparent",
              padding: isDesktop ? "0.25rem 0.5rem" : "0",
              borderRadius: "4px",
            }}
          >
            {isDesktop && `Added: ${dateAdded}`}
          </span>
        )}
      </div>

      {/* Description */}
      <p
        style={{
          fontSize: isDesktop ? "0.95rem" : "0.9rem",
          color: "#444",
          margin: "0.5rem 0 0 0",
          lineHeight: 1.6,
        }}
      >
        {description}
      </p>

      {/* URL + button */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "0.75rem",
        }}
      >
        <span
          style={{
            fontSize: "0.85rem",
            color: "#666",
          }}
        >
          {url.replace(/(^\w+:|^)\/\//, "").split("/")[0]}
        </span>

        <motion.a
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            backgroundColor: "#0066cc",
            color: "white",
            border: "none",
            borderRadius: "6px",
            padding: "0.5rem 1rem",
            fontSize: "0.9rem",
            fontWeight: "600",
            cursor: "pointer",
            textDecoration: "none",
            display: "inline-block",
          }}
        >
          Visit
        </motion.a>
      </div>
    </motion.div>
  );
};

export default ResourceSection;
